import { getApiClient } from "../httpClient";
import {
  ReferralCodeResponse,
  ReferralStatsResponse,
  ClaimReferralResponse,
  ReferralEarningsResponse,
  ReferralEarningsParams,
  XPBalanceResponse,
  XPHistoryResponse,
  XPHistoryParams,
  ClaimMilestoneResponse,
  MilestoneStatusResponse,
} from "./types";

/**
 * Extracts a user-friendly error message from an Axios error object.
 */
const extractErrorMessage = (error: any, fallback = "Unexpected error") =>
  error?.response?.data?.error?.message ||
  error?.response?.data?.message ||
  error?.message ||
  fallback;

// ============================================================================
// Referrals API
// ============================================================================

/**
 * Gets the user's referral code or generates one if they don't have one.
 * @returns The referral code response
 */
export const getReferralCode = async (): Promise<ReferralCodeResponse> => {
  try {
    const client = getApiClient();
    const response = await client.get<ReferralStatsResponse>(
      "/cards/referrals/code"
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to get referral code"));
  }
};

/**
 * Gets the user's referral statistics including total referrals and referral code.
 * @returns The referral statistics response
 */
export const getReferralStats = async (): Promise<ReferralStatsResponse> => {
  try {
    const client = getApiClient();
    const response = await client.get<ReferralStatsResponse>(
      "/cards/referrals/code"
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to get referral stats"));
  }
};

/**
 * Claims a referral using a referrer's code.
 * @param referrerCode - The referral code from the referrer
 * @returns The claim referral response
 */
export const claimReferral = async (
  referrerCode: string
): Promise<ClaimReferralResponse> => {
  try {
    const client = getApiClient();
    const response = await client.post<ClaimReferralResponse>(
      "/cards/referrals/claim",
      { referrerCode }
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to claim referral"));
  }
};

/**
 * Gets referral earnings with pagination.
 * @param params - Optional pagination parameters
 * @returns The referral earnings response
 */
export const getReferralEarnings = async (
  params?: ReferralEarningsParams
): Promise<ReferralEarningsResponse> => {
  try {
    const client = getApiClient();
    const response = await client.get<ReferralEarningsResponse>(
      "/cards/rewards/referral-earnings",
      { params }
    );
    return response.data;
  } catch (error) {
    throw new Error(
      extractErrorMessage(error, "Failed to get referral earnings")
    );
  }
};

// ============================================================================
// XP (Experience Points) API
// ============================================================================

/**
 * Retrieves the current XP balance for a specific user.
 * @returns The XP balance response
 */
export const getXPBalance = async (): Promise<XPBalanceResponse> => {
  try {
    const client = getApiClient();
    const response = await client.get<XPBalanceResponse>(
      `/cards/rewards/balance`
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to get XP balance"));
  }
};

/**
 * Retrieves XP transaction history with optional filtering and pagination.
 * @param params - Optional filtering and pagination parameters
 * @returns The XP history response
 */
export const getXPHistory = async (
  params?: XPHistoryParams
): Promise<XPHistoryResponse> => {
  try {
    const client = getApiClient();
    const response = await client.get<XPHistoryResponse>(
      "/cards/rewards/history",
      {
        params,
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to get XP history"));
  }
};

// ============================================================================
// Milestones API
// ============================================================================

/**
 * Allows a user to claim XP reward for completing a milestone.
 * @param milestoneId - Milestone ID (UUID)
 * @returns The claim milestone response
 */
export const claimMilestone = async (
  milestoneId: string
): Promise<ClaimMilestoneResponse> => {
  try {
    const client = getApiClient();
    const response = await client.post<ClaimMilestoneResponse>(
      `/cards/milestones/claim/${milestoneId}`
    );
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, "Failed to claim milestone"));
  }
};

/**
 * Retrieves the status of all milestones for a specific user.
 * @returns The milestone status response
 */
export const getMilestoneStatus =
  async (): Promise<MilestoneStatusResponse> => {
    try {
      const client = getApiClient();
      const response = await client.get<MilestoneStatusResponse>(
        `/cards/milestones`
      );
      return response.data;
    } catch (error) {
      throw new Error(
        extractErrorMessage(error, "Failed to get milestone status")
      );
    }
  };
